import DailyUsageReportPanel from "../components/DailyUsageReportPanel";
import { useState, useEffect } from "react";
import {
  Shield, Users, Search, TrendingUp, Mail, Loader2, Lock, Eye, EyeOff,
  DollarSign, Download, Crown, Package, Truck, BarChart3, Calendar, Gift,
  Send, Zap, MessageSquare, Reply, CheckCircle, Circle, RefreshCw, UserPlus, PenLine, Phone,
  AlertTriangle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color = "#00E676" }) {
  return (
    <div className="neo-card p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 flex items-center justify-center border flex-shrink-0" style={{ borderColor: `${color}40`, backgroundColor: `${color}10` }}>
          <Icon size={18} style={{ color }} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{label}</p>
          <p className="text-xl font-extrabold">{value ?? "—"}</p>
        </div>
      </div>
    </div>
  );
}

function RevenueCard({ label, amount }) {
  return (
    <div className="bg-[#0A0A0A] dark:bg-[#111] border border-[#00E676]/20 p-3 text-center">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</p>
      <p className="text-lg font-extrabold text-[#00E676]">{amount?.toFixed(2) ?? "0.00"}€</p>
    </div>
  );
}

// === Sub-components for admin tabs ===
function BlogAutoList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await axios.get(`${API}/admin/blog-articles`, { headers: { Authorization: `Bearer ${token}` } });
      setArticles(r.data.articles || []);
    } catch {} finally { setLoading(false); }
  };
  if (loading) return <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!articles.length) return <p className="text-muted-foreground text-sm text-center py-6">Aucun article genere. Cliquez sur "Generer un article".</p>;
  return <div className="space-y-2">{articles.map((a, i) => (
    <div key={i} className="neo-card p-3">
      <div className="flex items-center justify-between">
        <div><p className="font-bold text-sm">{a.title}</p><p className="text-xs text-muted-foreground">{a.generated_at?.slice(0,10)} — {a.read_time} — SEO: {a.seo_score}/100</p></div>
        <span className="text-[10px] px-2 py-0.5 border border-[#00E676]/50 text-[#00E676] font-bold">{a.category}</span>
      </div>
    </div>
  ))}</div>;
}

function SocialPostsList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await axios.get(`${API}/admin/social-posts`, { headers: { Authorization: `Bearer ${token}` } });
      setPosts(r.data.posts || []);
    } catch {} finally { setLoading(false); }
  };
  if (loading) return <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!posts.length) return <p className="text-muted-foreground text-sm text-center py-6">Aucun post genere. Cliquez sur "Generer un post".</p>;
  return <div className="space-y-3">{posts.map((p, i) => (
    <div key={i} className="neo-card p-4">
      <p className="font-bold text-sm mb-1">{p.topic}</p>
      <p className="text-xs text-muted-foreground mb-2">{p.generated_at?.slice(0,16)}</p>
      <div className="bg-[#0A0A0A] text-white p-3 text-sm whitespace-pre-wrap max-h-40 overflow-y-auto">{p.text?.slice(0, 500)}{p.text?.length > 500 ? "..." : ""}</div>
      <div className="mt-2 text-xs text-[#00E676]">{p.hashtags}</div>
      <button onClick={() => { navigator.clipboard.writeText(`${p.text}\n\n${p.hashtags}`); toast.success("Post copie !"); }} className="neo-button-secondary text-xs mt-2 px-3 py-1">Copier le post</button>
    </div>
  ))}</div>;
}

function AffiliateStatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await axios.get(`${API}/admin/affiliates`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  if (loading) return <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-muted-foreground text-sm text-center py-6">Chargement...</p>;
  const s = data.summary;
  return <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard icon={Users} label="Total affilies" value={s.total_affiliates} />
      <StatCard icon={TrendingUp} label="Clics totaux" value={s.total_clicks} />
      <StatCard icon={DollarSign} label="Conversions" value={s.total_conversions} color="#FFCC00" />
      <StatCard icon={DollarSign} label="Commissions dues" value={`${s.total_earnings.toFixed(2)} EUR`} color="#00E676" />
    </div>
    {data.affiliates.length > 0 && <div className="neo-card p-3 overflow-x-auto">
      <table className="w-full text-sm"><thead><tr className="border-b-2 border-[#0A0A0A] dark:border-[#00E676]/30">
        <th className="text-left py-2 px-2 text-xs font-bold uppercase">Nom</th><th className="text-left py-2 px-2 text-xs font-bold uppercase">Email</th>
        <th className="text-left py-2 px-2 text-xs font-bold uppercase">Code</th><th className="text-left py-2 px-2 text-xs font-bold uppercase">Clics</th>
        <th className="text-left py-2 px-2 text-xs font-bold uppercase">Conv.</th><th className="text-left py-2 px-2 text-xs font-bold uppercase">Gains</th>
      </tr></thead><tbody>{data.affiliates.map((a, i) => (
        <tr key={i} className="border-b border-white/5 hover:bg-[#00E676]/5">
          <td className="py-2 px-2 font-bold">{a.name}</td><td className="py-2 px-2 text-xs">{a.email}</td>
          <td className="py-2 px-2 text-xs text-[#00E676]">{a.code}</td><td className="py-2 px-2">{a.total_clicks}</td>
          <td className="py-2 px-2">{a.total_conversions}</td><td className="py-2 px-2 text-[#00E676] font-bold">{a.total_earnings?.toFixed(2)} EUR</td>
        </tr>
      ))}</tbody></table>
    </div>}
  </div>;
}

function ProspectsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await axios.get(`${API}/admin/prospects`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  if (loading) return <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-muted-foreground text-sm text-center py-6">Chargement...</p>;
  const s = data.summary;
  return <div className="space-y-4">
    <div className="grid grid-cols-3 gap-3">
      <StatCard icon={Users} label="Total prospects" value={s.total} />
      <StatCard icon={Mail} label="Contactes" value={s.contacted} color="#00E676" />
      <StatCard icon={Users} label="Non contactes" value={s.not_contacted} color="#FFCC00" />
    </div>
    {data.runs?.length > 0 && <div className="neo-card p-3">
      <h4 className="text-xs font-bold uppercase mb-2">Dernieres recherches</h4>
      {data.runs.slice(0, 5).map((r, i) => (
        <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
          <span>{r.query}</span><span>{r.found} trouves, {r.added} ajoutes — {r.run_at?.slice(0, 10)}</span>
        </div>
      ))}
    </div>}
    {data.prospects?.length > 0 && <div className="neo-card p-3 overflow-x-auto max-h-64 overflow-y-auto">
      <table className="w-full text-xs"><thead><tr className="border-b border-white/10">
        <th className="text-left py-1 px-2">Nom</th><th className="text-left py-1 px-2">Email</th><th className="text-left py-1 px-2">Plateforme</th><th className="text-left py-1 px-2">Score</th><th className="text-left py-1 px-2">Date</th>
      </tr></thead><tbody>{data.prospects.slice(0, 50).map((p, i) => (
        <tr key={i} className="border-b border-white/5">
          <td className="py-1 px-2">{p.name}</td><td className="py-1 px-2">{p.email}</td><td className="py-1 px-2">{p.platform}</td>
          <td className="py-1 px-2"><span className={p.score >= 7 ? "text-[#00E676]" : "text-muted-foreground"}>{p.score}/10</span></td>
          <td className="py-1 px-2 text-muted-foreground">{p.found_at?.slice(0, 10)}</td>
        </tr>
      ))}</tbody></table>
    </div>}
  </div>;
}

function UsageAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await axios.get(`${API}/admin/usage-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  if (loading) return <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-muted-foreground text-sm text-center py-6">Chargement...</p>;
  const s = data.summary;
  return <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard icon={Search} label="Scans aujourd'hui" value={s.scans_today} />
      <StatCard icon={Search} label="Scans 7j" value={s.scans_this_week} color="#00BCD4" />
      <StatCard icon={Users} label="Utilisateurs actifs 7j" value={s.active_users_7d} color="#FFCC00" />
      <StatCard icon={Search} label="Scans total" value={s.total_scans} />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard icon={BarChart3} label="Watchlist items" value={s.total_watchlist_items} />
      <StatCard icon={Gift} label="Spins total" value={s.total_spins} />
      <StatCard icon={Gift} label="Spins aujourd'hui" value={s.spins_today} color="#FFCC00" />
      <StatCard icon={Search} label="Comparaisons" value={s.total_compares} color="#00BCD4" />
    </div>
    {data.daily_breakdown?.length > 0 && <div className="neo-card p-3">
      <h4 className="text-xs font-bold uppercase mb-2">Activite quotidienne (14 jours)</h4>
      <div className="space-y-1">{data.daily_breakdown.map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-20 text-muted-foreground">{d.date?.slice(5)}</span>
          <div className="flex-1 bg-[#0A0A0A]/20 h-4 relative">
            <div className="bg-[#00E676] h-full" style={{ width: `${Math.min(100, (d.scans / Math.max(1, ...data.daily_breakdown.map(x => x.scans))) * 100)}%` }}></div>
          </div>
          <span className="w-16 text-right">{d.scans} scans</span>
          <span className="w-16 text-right text-[#FFCC00]">{d.spins} spins</span>
        </div>
      ))}</div>
    </div>}
    {data.top_searches?.length > 0 && <div className="neo-card p-3">
      <h4 className="text-xs font-bold uppercase mb-2">Top recherches</h4>
      {data.top_searches.map((t, i) => (
        <div key={i} className="flex justify-between text-xs py-1 border-b border-white/5">
          <span className="font-medium">{t.query}</span><span className="text-[#00E676]">{t.count}x</span>
        </div>
      ))}
    </div>}
  </div>;
}

function SegmentsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const r = await axios.get(`${API}/admin/contacts-segmented`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  if (loading) return <div className="text-center py-8"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return <p className="text-muted-foreground text-sm text-center py-6">Chargement...</p>;
  const s = data.segments;
  const tagColors = { abonne: "#00E676", non_abonne: "#FFCC00", newsletter_only: "#00BCD4" };
  const tagLabels = { abonne: "Abonne", non_abonne: "Non-abonne", newsletter_only: "Newsletter seul" };
  return <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <StatCard icon={Crown} label="Abonnes" value={s.abonnes} color="#00E676" />
      <StatCard icon={Users} label="Non-abonnes" value={s.non_abonnes} color="#FFCC00" />
      <StatCard icon={Mail} label="Newsletter seul" value={s.newsletter_only} color="#00BCD4" />
      <StatCard icon={Users} label="Total contacts" value={s.total} />
    </div>
    <div className="neo-card p-3 max-h-80 overflow-y-auto">
      <table className="w-full text-xs"><thead><tr className="border-b border-white/10">
        <th className="text-left py-1 px-2">Email</th><th className="text-left py-1 px-2">Nom</th><th className="text-left py-1 px-2">Tag</th><th className="text-left py-1 px-2">Newsletter</th>
      </tr></thead><tbody>{data.contacts?.slice(0, 100).map((c, i) => (
        <tr key={i} className="border-b border-white/5">
          <td className="py-1 px-2 font-medium">{c.email}</td><td className="py-1 px-2">{c.name || "—"}</td>
          <td className="py-1 px-2"><span className="text-[10px] px-2 py-0.5 font-bold uppercase border" style={{ borderColor: `${tagColors[c.tag]}50`, color: tagColors[c.tag] }}>{tagLabels[c.tag] || c.tag}</span></td>
          <td className="py-1 px-2">{c.in_newsletter ? <span className="text-[#00E676]">Oui</span> : <span className="text-muted-foreground">Non</span>}</td>
        </tr>
      ))}</tbody></table>
    </div>
  </div>;
}

export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [spinWins, setSpinWins] = useState(null);
  const [tab, setTab] = useState("overview");
  const [grantSpinEmail, setGrantSpinEmail] = useState("");
  const [grantSpinCount, setGrantSpinCount] = useState(3);
  const [grantSpinLoading, setGrantSpinLoading] = useState(false);
  const [grantAccessEmail, setGrantAccessEmail] = useState("");
  const [grantAccessDays, setGrantAccessDays] = useState(30);
  const [grantAccessLoading, setGrantAccessLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesUnread, setMessagesUnread] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyLoading, setReplyLoading] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [refreshOppsLoading, setRefreshOppsLoading] = useState(false);
  const [syncPaymentsLoading, setSyncPaymentsLoading] = useState(false);
  // Newsletter manual add
  const [nlAddEmail, setNlAddEmail] = useState("");
  const [nlAddName, setNlAddName] = useState("");
  const [nlAddLoading, setNlAddLoading] = useState(false);
  // Send email campaign
  const [campaignSubject, setCampaignSubject] = useState("");
  const [campaignBody, setCampaignBody] = useState("");
  const [campaignTarget, setCampaignTarget] = useState("newsletter");
  const [campaignCustomEmails, setCampaignCustomEmails] = useState("");
  const [campaignLoading, setCampaignLoading] = useState(false);
  // Segment counts
  const [segmentCounts, setSegmentCounts] = useState({});
  const [errorLogs, setErrorLogs] = useState([]);
  const [selectedUserAccess, setSelectedUserAccess] = useState(null);
  const [userAccessLoading, setUserAccessLoading] = useState(false);
  // Telegram
  const [telegramSubs, setTelegramSubs] = useState([]);
  const [telegramMsg, setTelegramMsg] = useState("");
  const [telegramTarget, setTelegramTarget] = useState("all");
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [nlStats, setNlStats] = useState(null);
  const [nlStatsLoading, setNlStatsLoading] = useState(false);
  const [triggerNlLoading, setTriggerNlLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setAuthenticated(true);
        localStorage.setItem("admin_token", response.data.token);
        toast.success("Accès admin autorisé");
        fetchAdminData(response.data.token);
      }
    } catch { toast.error("Mot de passe incorrect"); }
    finally { setLoginLoading(false); }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) { setAuthenticated(true); fetchAdminData(token); }
  }, []);

  const getHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchUserAccess = async (email) => {
    setUserAccessLoading(true);
    try {
      const res = await axios.get(`${API}/admin/user-access/${encodeURIComponent(email)}`, { headers: getHeaders() });
      setSelectedUserAccess(res.data);
    } catch (e) {
      toast.error("Erreur chargement acces");
    }
    setUserAccessLoading(false);
  };

  const togglePurchase = async (email, productType, activate) => {
    try {
      await axios.post(`${API}/admin/toggle-purchase`, { email, product_type: productType, activate }, { headers: getHeaders() });
      toast.success(activate ? "Acces active" : "Acces revoque");
      fetchUserAccess(email);
    } catch { toast.error("Erreur"); }
  };

  const toggleSubscription = async (email, activate) => {
    try {
      await axios.post(`${API}/admin/toggle-subscription`, { email, activate }, { headers: getHeaders() });
      toast.success(activate ? "Abonnement active" : "Abonnement revoque");
      fetchUserAccess(email);
    } catch { toast.error("Erreur"); }
  };

  const fetchAdminData = async (token) => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, usersRes, subsRes, nlRes, spinsRes, msgsRes, revRes, cronRes, tgRes, segRes, errRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/subscriptions`, { headers }),
        axios.get(`${API}/admin/newsletter-list`, { headers }),
        axios.get(`${API}/admin/spin-wins`, { headers }),
        axios.get(`${API}/admin/messages`, { headers }).catch(() => ({ data: { messages: [], unread: 0 } })),
        axios.get(`${API}/admin/revenue`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/cron-status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/telegram-subscribers`, { headers }).catch(() => ({ data: { subscribers: [], total: 0 } })),
        axios.get(`${API}/admin/segment-counts`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/error-logs?limit=500`, { headers }).catch(() => ({ data: { logs: [], total: 0 } })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setSubscriptions(subsRes.data.subscriptions || []);
      setNewsletter(nlRes.data.subscribers || []);
      setSpinWins(spinsRes.data);
      setMessages(msgsRes.data.messages || []);
      setMessagesUnread(msgsRes.data.unread || 0);
      setRevenue(revRes.data);
      setCronStatus(cronRes.data);
      setTelegramSubs(tgRes.data.subscribers || []);
      setSegmentCounts(segRes.data || {});
      setErrorLogs(errRes.data.logs || []);
    } catch (error) {
      if (error.response?.status === 401) {
        setAuthenticated(false);
        localStorage.removeItem("admin_token");
        toast.error("Session expirée");
      }
    }
    setLoading(false);
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem("admin_token");
    window.open(`${API}/admin/export/csv?token=${token}`, "_blank");
  };

  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem("admin_token");
    setPassword("");
    setStats(null);
  };

  const handleReply = async (msg) => {
    if (!replyText.trim()) return;
    setReplyLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/messages/reply`, {
        email: msg.email,
        subject: msg.subject,
        message: replyText,
        original_created_at: msg.created_at,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Réponse envoyée à ${msg.email}`);
      setReplyTo(null);
      setReplyText("");
      fetchAdminData(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleGrantSpins = async (e) => {
    e.preventDefault();
    if (!grantSpinEmail) return;
    setGrantSpinLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/admin/grant-spins`, {
        email: grantSpinEmail,
        spins: grantSpinCount,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.spins_added} spins attribués à ${res.data.email} (total: ${res.data.total_credits})`);
      setGrantSpinEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setGrantSpinLoading(false);
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantAccessEmail) return;
    setGrantAccessLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(`${API}/admin/grant-access`, {
        email: grantAccessEmail,
        days: grantAccessDays,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const expDate = new Date(res.data.expires_at).toLocaleDateString("fr-FR");
      toast.success(`Accès ${res.data.days}j accordé à ${res.data.email} (expire: ${expDate})`);
      setGrantAccessEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setGrantAccessLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="admin-login">
        <div className="neo-card p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-[#0A0A0A] dark:bg-[#00E676] flex items-center justify-center mb-4">
              <Shield className="text-[#00E676] dark:text-[#0A0A0A]" size={32} />
            </div>
            <h1 className="text-2xl font-extrabold">ADMIN</h1>
            <p className="text-sm text-muted-foreground mt-1">Accès réservé</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input type={showPassword ? "text" : "password"} placeholder="Mot de passe admin" value={password} onChange={(e) => setPassword(e.target.value)} className="neo-input w-full pl-12 pr-12" data-testid="admin-password-input" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button type="submit" disabled={loginLoading || !password} className="neo-button-primary w-full flex items-center justify-center gap-2" data-testid="admin-login-btn">
              {loginLoading ? <Loader2 size={18} className="animate-spin" /> : <Shield size={18} />} Accéder
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#00E676]" size={32} /></div>;
  }

  const rev = stats?.revenue || {};

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3">
            <Shield className="text-[#00E676]" size={36} /> ADMIN
          </h1>
          <p className="text-muted-foreground mt-1 text-sm uppercase tracking-wider">Tableau de bord administrateur</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportCSV} className="neo-button-primary flex items-center gap-2 text-xs" data-testid="export-csv-btn">
            <Download size={16} /> Export CSV
          </button>
          <button onClick={handleLogout} className="neo-button-secondary text-xs px-3">Déconnexion</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto">
        {[
          { id: "overview", label: "Vue d'ensemble" },
          { id: "users", label: `Clients (${users.length})` },
          { id: "subs", label: `Abonnés (${subscriptions.length})` },
          { id: "newsletter", label: `Newsletter (${newsletter.length})` },
          { id: "spin_wins", label: `Roue (${spinWins?.total_spins ?? 0})` },
          { id: "revenue", label: "Revenus" },
          { id: "messages", label: `Messages${messagesUnread > 0 ? ` (${messagesUnread})` : ""}` },
          { id: "cron", label: "CRON" },
          { id: "newsletter_add", label: "Newsletter+" },
          { id: "nl_stats", label: "Stats NL" },
          { id: "send_email", label: "Envoyer Mail" },
          { id: "telegram", label: `Telegram (${telegramSubs.length})` },
          { id: "actions", label: "Actions" },
          { id: "errors", label: "Erreurs" },
          { id: "blog_auto", label: "Blog Auto" },
          { id: "social", label: "Reseaux" },
          { id: "affiliates", label: "Affilies" },
          { id: "prospects", label: "Prospects" },
          { id: "usage", label: "Usage" },
          { id: "segments", label: "Segments" },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)} className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border-2 whitespace-nowrap ${tab === id ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A]" : "border-[#0A0A0A] dark:border-[#00E676]/30 hover:bg-[#00E676]/10"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <StatCard icon={Users} label="Utilisateurs" value={stats.total_users} />
            <StatCard icon={Users} label="Nouveaux (24h)" value={stats.users_today} color="#FFCC00" />
            <StatCard icon={Crown} label="Abonnés actifs" value={stats.active_subscriptions} color="#00E676" />
            <StatCard icon={Crown} label="Abos annulés" value={stats.cancelled_subscriptions} color="#FF4444" />
            <StatCard icon={Package} label="500 Produits vendus" value={stats.products_purchased} />
            <StatCard icon={Truck} label="300 Fourn. vendus" value={stats.fournisseurs_purchased} />
            <StatCard icon={Mail} label="Newsletter" value={stats.newsletter_subscribers} color="#FFCC00" />
            <StatCard icon={Search} label="Scans (24h)" value={stats.searches_today} />
            <StatCard icon={Search} label="Scans (total)" value={stats.total_searches} />
            <StatCard icon={BarChart3} label="Watchlist" value={stats.total_watchlist} />
            <StatCard icon={Mail} label="Alertes Email" value={stats.email_subscribers} />
            <StatCard icon={Mail} label="Alertes Telegram" value={stats.telegram_subscribers} />
          </div>

          {/* Revenue */}
          <div className="neo-card p-4 sm:p-6" data-testid="revenue-section">
            <h2 className="text-base font-bold uppercase tracking-wider flex items-center gap-2 mb-4">
              <DollarSign size={18} className="text-[#00E676]" /> Revenus
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <RevenueCard label="Aujourd'hui" amount={rev.today} />
              <RevenueCard label="7 jours" amount={rev.week} />
              <RevenueCard label="14 jours" amount={rev.two_weeks} />
              <RevenueCard label="30 jours" amount={rev.month} />
              <RevenueCard label="12 mois" amount={rev.year} />
              <RevenueCard label="Total" amount={rev.total} />
            </div>
          </div>
        </>
      )}

      {tab === "users" && (<>
        <div className="neo-card p-4 overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A] dark:border-[#00E676]/30">
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Email</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Nom</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Auth</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Rôle</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Inscription</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Detail</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Acces</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i} className="border-b border-[#0A0A0A]/10 dark:border-white/5 hover:bg-[#00E676]/5">
                  <td className="py-2 px-3 font-bold">{u.email}</td>
                  <td className="py-2 px-3">{u.name || "—"}</td>
                  <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 border font-bold uppercase ${u.auth_method === "google" ? "border-blue-500/40 text-blue-500" : "border-[#00E676]/40 text-[#00E676]"}`}>{u.auth_method}</span></td>
                  <td className="py-2 px-3">{u.role}</td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">{u.created_at?.slice(0, 10)}</td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => fetchUserAccess(u.email)}
                      className="text-[10px] px-2 py-0.5 font-bold uppercase border border-[#00BCD4]/50 text-[#00BCD4] hover:bg-[#00BCD4]/10"
                      data-testid={`view-access-${i}`}
                    >
                      Voir acces
                    </button>
                  </td>
                  <td className="py-2 px-3">
                    {u.email !== "torregrossa.joseph@gmail.com" && (
                      <button
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem("admin_token");
                            await axios.post(`${API}/admin/toggle-access`, {
                              email: u.email, blocked: !u.blocked,
                            }, { headers: { Authorization: `Bearer ${token}` } });
                            toast.success(u.blocked ? `${u.email} debloque` : `${u.email} bloque`);
                            fetchAdminData(token);
                          } catch { toast.error("Erreur"); }
                        }}
                        className={`text-[10px] px-2 py-0.5 font-bold uppercase border ${u.blocked ? "border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10" : "border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10"}`}
                        data-testid={`toggle-access-${i}`}
                      >
                        {u.blocked ? "Debloque" : "Bloquer"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* User Access Detail Panel */}
        {selectedUserAccess && (
          <div className="neo-card p-4 mt-4 border-[#00BCD4]" data-testid="user-access-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-sm">
                Acces de {selectedUserAccess.user?.email}
              </h3>
              <button onClick={() => setSelectedUserAccess(null)} className="text-muted-foreground hover:text-foreground text-lg font-bold px-2">X</button>
            </div>
            {userAccessLoading ? <Loader2 className="animate-spin mx-auto" /> : (
              <div className="space-y-4">
                {/* Subscription */}
                <div className="border border-[#333] p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-xs uppercase text-muted-foreground">Abonnement Pro</p>
                      <p className="text-sm">{selectedUserAccess.subscription ? (
                        <span className={selectedUserAccess.subscription.status === "active" ? "text-[#00E676]" : "text-red-500"}>
                          {selectedUserAccess.subscription.plan || "monthly_19"} — {selectedUserAccess.subscription.status}
                        </span>
                      ) : <span className="text-muted-foreground">Aucun</span>}</p>
                    </div>
                    <button
                      onClick={() => toggleSubscription(selectedUserAccess.user.email, !selectedUserAccess.subscription || selectedUserAccess.subscription.status !== "active")}
                      className={`text-[10px] px-3 py-1 font-bold uppercase border ${
                        selectedUserAccess.subscription?.status === "active"
                          ? "border-red-500/50 text-red-500 hover:bg-red-500/10"
                          : "border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10"
                      }`}
                    >
                      {selectedUserAccess.subscription?.status === "active" ? "Revoquer" : "Activer"}
                    </button>
                  </div>
                </div>

                {/* Upsells */}
                <div className="border border-[#333] p-3">
                  <p className="font-bold text-xs uppercase text-muted-foreground mb-2">Upsells / Produits</p>
                  {[
                    { key: "opportunities_premium", label: "Opportunites Premium (4.90 EUR)" },
                    { key: "products_500", label: "500 Produits Gagnants (9 EUR)" },
                    { key: "fournisseurs_300", label: "300 Fournisseurs (9 EUR)" },
                  ].map(({ key, label }) => {
                    const purchase = (selectedUserAccess.purchases || []).find(p => p.product_type === key);
                    const isActive = purchase?.status === "active";
                    return (
                      <div key={key} className="flex items-center justify-between py-1.5 border-b border-[#222] last:border-0">
                        <div>
                          <p className="text-sm">{label}</p>
                          <p className="text-xs text-muted-foreground">
                            {purchase ? `${isActive ? "Actif" : "Inactif"} — Source: ${purchase.source || "achat"} — ${purchase.created_at?.slice(0,10) || ""}` : "Non achete"}
                          </p>
                        </div>
                        <button
                          onClick={() => togglePurchase(selectedUserAccess.user.email, key, !isActive)}
                          className={`text-[10px] px-3 py-1 font-bold uppercase border ${
                            isActive
                              ? "border-red-500/50 text-red-500 hover:bg-red-500/10"
                              : "border-[#00E676]/50 text-[#00E676] hover:bg-[#00E676]/10"
                          }`}
                        >
                          {isActive ? "Revoquer" : "Activer"}
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Spin Credits & History */}
                <div className="border border-[#333] p-3">
                  <p className="font-bold text-xs uppercase text-muted-foreground mb-2">
                    Roue de la Fortune — {selectedUserAccess.spin_credits} spin(s) restant(s)
                  </p>
                  {(selectedUserAccess.spin_history || []).length > 0 ? (
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {selectedUserAccess.spin_history.slice(0, 10).map((s, si) => (
                        <div key={si} className="flex items-center justify-between text-xs">
                          <span>{s.prize_label}</span>
                          <span className="text-muted-foreground">{s.timestamp?.slice(0, 10)} {s.paid ? "(Payant)" : "(Gratuit)"}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-muted-foreground">Aucun spin effectue</p>}
                </div>

                {/* Payment History */}
                <div className="border border-[#333] p-3">
                  <p className="font-bold text-xs uppercase text-muted-foreground mb-2">
                    Historique paiements ({(selectedUserAccess.payments || []).length})
                  </p>
                  {(selectedUserAccess.payments || []).length > 0 ? (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {selectedUserAccess.payments.slice(0, 15).map((p, pi) => (
                        <div key={pi} className="flex items-center justify-between text-xs border-b border-[#222] last:border-0 py-1">
                          <span className="font-medium">{p.payment_type || p.metadata?.type || "?"}</span>
                          <span>{p.amount} EUR</span>
                          <span className={p.payment_status === "paid" ? "text-[#00E676]" : "text-[#FFCC00]"}>{p.payment_status}</span>
                          <span className="text-muted-foreground">{p.created_at?.slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-xs text-muted-foreground">Aucun paiement</p>}
                </div>
              </div>
            )}
          </div>
        )}
      </>)}

      {tab === "subs" && (
        <div className="neo-card p-4 overflow-x-auto" data-testid="subs-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A] dark:border-[#00E676]/30">
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Email</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Plan</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Statut</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Date</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((s, i) => (
                <tr key={i} className="border-b border-[#0A0A0A]/10 dark:border-white/5 hover:bg-[#00E676]/5">
                  <td className="py-2 px-3 font-bold">{s.email}</td>
                  <td className="py-2 px-3">{s.plan}</td>
                  <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 border font-bold uppercase ${s.status === "active" ? "border-[#00E676]/40 text-[#00E676]" : "border-red-500/40 text-red-500"}`}>{s.status}</span></td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">{s.activated_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {subscriptions.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-muted-foreground">Aucun abonné</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "newsletter" && (
        <div className="neo-card p-4 overflow-x-auto" data-testid="newsletter-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A] dark:border-[#00E676]/30">
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Email</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Source</th>
                <th className="text-left py-2 px-3 font-bold uppercase text-xs">Date</th>
              </tr>
            </thead>
            <tbody>
              {newsletter.map((n, i) => (
                <tr key={i} className="border-b border-[#0A0A0A]/10 dark:border-white/5 hover:bg-[#00E676]/5">
                  <td className="py-2 px-3 font-bold">{n.email}</td>
                  <td className="py-2 px-3 text-xs">{n.source || "landing"}</td>
                  <td className="py-2 px-3 text-xs text-muted-foreground">{n.subscribed_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {newsletter.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-muted-foreground">Aucun inscrit</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "spin_wins" && spinWins && (
        <>
          {/* Spin Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={Gift} label="Total spins" value={spinWins.total_spins} />
            <StatCard icon={Gift} label="Spins gratuits" value={spinWins.free_spins} color="#FFCC00" />
            <StatCard icon={DollarSign} label="Spins payants" value={spinWins.paid_spins} color="#00E676" />
            <StatCard icon={Gift} label="Lots distincts" value={Object.keys(spinWins.prize_distribution || {}).length} color="#FF6B6B" />
          </div>

          {/* Prize Distribution */}
          {spinWins.prize_distribution && Object.keys(spinWins.prize_distribution).length > 0 && (
            <div className="neo-card p-4" data-testid="prize-distribution">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <BarChart3 size={16} className="text-[#00E676]" /> Distribution des lots
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(spinWins.prize_distribution).map(([prize, count]) => (
                  <div key={prize} className="bg-[#0A0A0A] dark:bg-[#111] border border-[#00E676]/20 p-3 text-center">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1 truncate" title={prize}>{prize}</p>
                    <p className="text-lg font-extrabold text-[#00E676]">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Wins Table */}
          <div className="neo-card p-4 overflow-x-auto" data-testid="spin-wins-table">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Gift size={16} className="text-[#FFCC00]" /> Historique des gains
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#0A0A0A] dark:border-[#00E676]/30">
                  <th className="text-left py-2 px-3 font-bold uppercase text-xs">Email</th>
                  <th className="text-left py-2 px-3 font-bold uppercase text-xs">Lot</th>
                  <th className="text-left py-2 px-3 font-bold uppercase text-xs">Type</th>
                  <th className="text-left py-2 px-3 font-bold uppercase text-xs">Distribué</th>
                  <th className="text-left py-2 px-3 font-bold uppercase text-xs">Date</th>
                </tr>
              </thead>
              <tbody>
                {(spinWins.wins || []).map((w, i) => (
                  <tr key={i} className="border-b border-[#0A0A0A]/10 dark:border-white/5 hover:bg-[#00E676]/5">
                    <td className="py-2 px-3 font-bold">{w.email}</td>
                    <td className="py-2 px-3">{w.prize_label || w.prize || "—"}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 border font-bold uppercase ${w.paid ? "border-[#00E676]/40 text-[#00E676]" : "border-[#FFCC00]/40 text-[#FFCC00]"}`}>
                        {w.paid ? "Payant" : "Gratuit"}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 border font-bold uppercase ${w.delivery?.delivered ? "border-[#00E676]/40 text-[#00E676]" : "border-red-500/40 text-red-500"}`}>
                        {w.delivery?.delivered ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{w.timestamp?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
                {(!spinWins.wins || spinWins.wins.length === 0) && (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Aucun gain enregistré</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "revenue" && (
        <div className="space-y-4" data-testid="admin-revenue">
          {/* Sync Button */}
          <div className="flex justify-end">
            <button
              onClick={async () => {
                setSyncPaymentsLoading(true);
                try {
                  const r = await axios.post(`${API}/admin/sync-payments`, {}, { headers: getHeaders() });
                  const msg = r.data.synced > 0
                    ? `${r.data.synced} paiement(s) synchronise(s) avec succes`
                    : `Aucun nouveau paiement a synchroniser (${r.data.total_checked} verifies)`;
                  toast.success(msg);
                  fetchAdminData(localStorage.getItem("admin_token"));
                } catch (e) {
                  toast.error("Erreur de synchronisation: " + (e.response?.data?.detail || e.message));
                }
                setSyncPaymentsLoading(false);
              }}
              disabled={syncPaymentsLoading}
              className="neo-button-secondary text-xs flex items-center gap-2"
              data-testid="sync-payments-btn"
            >
              {syncPaymentsLoading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
              Synchroniser avec Stripe
            </button>
          </div>
          {/* Revenue Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={DollarSign} label="Revenu total" value={`${revenue?.total_revenue?.toFixed(2) || "0.00"}€`} />
            <StatCard icon={BarChart3} label="Transactions" value={revenue?.total_transactions || 0} color="#FFCC00" />
            <StatCard icon={Crown} label="Abonnements" value={`${revenue?.breakdown?.subscription?.total?.toFixed(2) || "0.00"}€`} color="#00BCD4" />
            <StatCard icon={Gift} label="Spins" value={`${revenue?.breakdown?.spin_purchase?.total?.toFixed(2) || "0.00"}€`} color="#FF6B35" />
          </div>

          {/* Breakdown cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { key: "subscription", label: "Abonnements 19€/mois", color: "#00E676" },
              { key: "products_500", label: "500 Produits 9€", color: "#FFCC00" },
              { key: "fournisseurs_300", label: "318 Fournisseurs 9€", color: "#00BCD4" },
              { key: "opportunities_premium", label: "Opportunites 4.90€", color: "#FF6B35" },
              { key: "spin_purchase", label: "Spins 2€+", color: "#E040FB" },
            ].map(({ key, label, color }) => (
              <div key={key} className="neo-card p-4 text-center">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-2 truncate" title={label}>{label}</p>
                <p className="text-2xl font-extrabold" style={{ color }}>{revenue?.breakdown?.[key]?.total?.toFixed(2) || "0.00"}€</p>
                <p className="text-xs text-muted-foreground mt-1">{revenue?.breakdown?.[key]?.count || 0} ventes</p>
              </div>
            ))}
          </div>

          {/* Recent transactions */}
          {revenue?.recent_transactions?.length > 0 && (
            <div className="neo-card p-4 overflow-x-auto">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
                <DollarSign size={16} className="text-[#00E676]" /> Dernières transactions
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#00E676]/30">
                    <th className="text-left py-2 px-3 font-bold uppercase text-xs">Email</th>
                    <th className="text-left py-2 px-3 font-bold uppercase text-xs">Type</th>
                    <th className="text-left py-2 px-3 font-bold uppercase text-xs">Montant</th>
                    <th className="text-left py-2 px-3 font-bold uppercase text-xs">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.recent_transactions.slice(0, 20).map((tx, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-[#00E676]/5">
                      <td className="py-2 px-3 font-bold">{tx.email}</td>
                      <td className="py-2 px-3">
                        <span className="text-xs px-2 py-0.5 border border-[#00E676]/40 text-[#00E676] font-bold uppercase">
                          {tx.payment_type || "—"}
                        </span>
                      </td>
                      <td className="py-2 px-3 font-bold text-[#00E676]">{tx.amount?.toFixed(2)}€</td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">{tx.paid_at?.slice(0, 16).replace("T", " ") || tx.created_at?.slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-3" data-testid="admin-messages">
          {messages.length === 0 && (
            <div className="neo-card p-12 text-center text-muted-foreground">Aucun message recu</div>
          )}
          {messages.map((msg, i) => {
            const isReplying = replyTo === i;
            return (
              <div key={i} className={`neo-card p-4 ${msg.status === "unread" ? "border-[#FFCC00]" : msg.status === "replied" ? "border-[#00E676]/30" : ""}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {msg.status === "unread" ? (
                      <Circle size={10} className="text-[#FFCC00] fill-[#FFCC00] flex-shrink-0" />
                    ) : msg.status === "replied" ? (
                      <CheckCircle size={14} className="text-[#00E676] flex-shrink-0" />
                    ) : (
                      <Circle size={10} className="text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="font-bold text-sm truncate">{msg.name} — <span className="text-muted-foreground font-normal">{msg.email}</span></p>
                      <p className="text-xs text-[#00E676] font-bold">{msg.subject}</p>
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground flex-shrink-0">{msg.created_at?.slice(0, 10)}</span>
                </div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap mb-3 pl-6">{msg.message}</p>
                {msg.replies?.length > 0 && (
                  <div className="pl-6 space-y-2 mb-3">
                    {msg.replies.map((r, ri) => (
                      <div key={ri} className="bg-[#00E676]/5 border-l-2 border-[#00E676] pl-3 py-2">
                        <p className="text-xs text-[#00E676] font-bold mb-1">Votre reponse — {r.sent_at?.slice(0, 10)}</p>
                        <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 pl-6">
                  <button
                    onClick={() => { setReplyTo(isReplying ? null : i); setReplyText(""); }}
                    className={`text-xs font-bold uppercase px-3 py-1.5 border-2 ${isReplying ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A]" : "border-[#00E676]/30 hover:bg-[#00E676]/10"}`}
                  >
                    <Reply size={12} className="inline mr-1" /> Repondre
                  </button>
                </div>
                {isReplying && (
                  <div className="mt-3 pl-6 space-y-2">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Votre réponse..."
                      rows={3}
                      className="neo-input w-full p-3 text-sm resize-none"
                      data-testid="reply-textarea"
                    />
                    <button
                      onClick={() => handleReply(msg)}
                      disabled={replyLoading || !replyText.trim()}
                      className="neo-button-primary text-sm px-4 py-2 flex items-center gap-2"
                      data-testid="reply-send-btn"
                    >
                      {replyLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      Envoyer
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "cron" && (
        <div className="space-y-6" data-testid="admin-cron">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Daily Discovery Status */}
            <div className="neo-card p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                <Zap size={16} className="text-[#FFCC00]" />
                Trouvaille Journaliere
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Dernier scan :</span>
                  <span className="font-bold">{cronStatus?.daily_discovery?.last_run ? new Date(cronStatus.daily_discovery.last_run).toLocaleString("fr-FR") : "Jamais"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Dernier produit :</span>
                  <span className="font-bold">{cronStatus?.daily_discovery?.last_product || "—"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total trouvailles :</span>
                  <span className="font-bold text-[#00E676]">{cronStatus?.daily_discovery?.total_discoveries ?? 0}</span>
                </div>
                <button
                  onClick={async () => {
                    setTriggerLoading(true);
                    try {
                      const token = localStorage.getItem("admin_token");
                      await axios.post(`${API}/admin/trigger-daily-discovery`, {}, { headers: { Authorization: `Bearer ${token}` } });
                      toast.success("Scan journalier lance en arriere-plan");
                      setTimeout(() => fetchAdminData(token), 5000);
                    } catch { toast.error("Erreur"); }
                    finally { setTriggerLoading(false); }
                  }}
                  disabled={triggerLoading}
                  className="neo-button-primary w-full flex items-center justify-center gap-2 mt-3"
                  data-testid="trigger-daily-discovery"
                >
                  {triggerLoading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                  Lancer manuellement
                </button>
              </div>
            </div>

            {/* Marketing Emails Status */}
            <div className="neo-card p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
                <Mail size={16} className="text-[#00BCD4]" />
                Emails Marketing Auto
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Dernier envoi :</span>
                  <span className="font-bold">{cronStatus?.marketing_emails?.last_sent ? new Date(cronStatus.marketing_emails.last_sent).toLocaleString("fr-FR") : "Jamais"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total envoyes :</span>
                  <span className="font-bold text-[#00BCD4]">{cronStatus?.marketing_emails?.total_sent ?? 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Desabonnements :</span>
                  <span className="font-bold text-[#FF6B35]">{cronStatus?.marketing_emails?.total_optouts ?? 0}</span>
                </div>
                <div className="bg-[#0A0A0A]/5 dark:bg-[#00E676]/5 border border-[#0A0A0A]/10 p-3 mt-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Frequence</p>
                  <p className="text-sm font-bold">Hebdomadaire (tous les 7 jours)</p>
                  <p className="text-[10px] text-muted-foreground mt-1">Envoye aux non-abonnes uniquement</p>
                </div>
              </div>
            </div>
          </div>

          <div className="neo-card p-4 bg-[#0A0A0A]/5 dark:bg-[#00E676]/5">
            <p className="text-xs text-muted-foreground">
              <strong>CRON actif :</strong> La trouvaille du jour est scannee automatiquement toutes les 24h et envoyee aux abonnes (email + Telegram).
              Les emails marketing hebdomadaires proposent l'abonnement aux utilisateurs non-Premium.
              Les opportunites sont rafraichies automatiquement tous les 3 jours.
            </p>
          </div>

          {/* Refresh Opportunities */}
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <RefreshCw size={16} className="text-[#00E676]" />
              Rafraichir les opportunites
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Scanne tous les produits tendance et met a jour le cache. Les acheteurs de l'upsell seront notifies par email.
            </p>
            <button
              onClick={async () => {
                setRefreshOppsLoading(true);
                try {
                  const token = localStorage.getItem("admin_token");
                  await axios.post(`${API}/admin/refresh-opportunities`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Refresh des opportunites lance en arriere-plan");
                } catch { toast.error("Erreur"); }
                finally { setRefreshOppsLoading(false); }
              }}
              disabled={refreshOppsLoading}
              className="neo-button-primary w-full flex items-center justify-center gap-2"
              data-testid="refresh-opportunities-btn"
            >
              {refreshOppsLoading ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Mettre a jour maintenant
            </button>
          </div>
        </div>
      )}

      {tab === "newsletter_add" && (
        <div className="space-y-6" data-testid="admin-newsletter-add">
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <UserPlus size={16} className="text-[#00E676]" />
              Ajouter un membre a la newsletter
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!nlAddEmail) return;
              setNlAddLoading(true);
              try {
                const token = localStorage.getItem("admin_token");
                const res = await axios.post(`${API}/admin/newsletter/add`, {
                  email: nlAddEmail, name: nlAddName,
                }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(res.data.message);
                setNlAddEmail(""); setNlAddName("");
                fetchAdminData(token);
              } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
              finally { setNlAddLoading(false); }
            }} className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Email</label>
                <Input type="email" placeholder="email@exemple.com" value={nlAddEmail} onChange={(e) => setNlAddEmail(e.target.value)} className="neo-input w-full" data-testid="nl-add-email" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Nom (optionnel)</label>
                <Input type="text" placeholder="Prenom Nom" value={nlAddName} onChange={(e) => setNlAddName(e.target.value)} className="neo-input w-full" data-testid="nl-add-name" />
              </div>
              <button type="submit" disabled={nlAddLoading || !nlAddEmail} className="neo-button-primary w-full flex items-center justify-center gap-2" data-testid="nl-add-submit">
                {nlAddLoading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
                Ajouter
              </button>
            </form>
          </div>

          {/* Current newsletter list */}
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Mail size={16} className="text-[#00BCD4]" />
              Membres newsletter ({newsletter.length})
            </h3>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {newsletter.map((n, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#0A0A0A]/10 text-sm">
                  <div>
                    <span className="font-bold">{n.email}</span>
                    {n.name && <span className="text-muted-foreground ml-2">({n.name})</span>}
                  </div>
                  <span className={`text-[10px] font-bold uppercase ${n.subscribed ? "text-[#00E676]" : "text-[#FF6B35]"}`}>
                    {n.subscribed ? "Actif" : "Inactif"}
                  </span>
                </div>
              ))}
              {newsletter.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aucun membre</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "nl_stats" && (
        <div className="space-y-6" data-testid="admin-nl-stats">
          <div className="neo-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                <BarChart3 size={16} className="text-[#00E676]" />
                Statistiques Newsletter
              </h3>
              <button
                onClick={async () => {
                  setNlStatsLoading(true);
                  try {
                    const token = localStorage.getItem("admin_token");
                    const res = await axios.get(`${API}/admin/newsletter-stats`, { headers: { Authorization: `Bearer ${token}` } });
                    setNlStats(res.data);
                  } catch { toast.error("Erreur chargement stats"); }
                  finally { setNlStatsLoading(false); }
                }}
                disabled={nlStatsLoading}
                className="neo-button-secondary text-xs px-3 py-1.5 flex items-center gap-1"
                data-testid="nl-stats-refresh"
              >
                {nlStatsLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Charger
              </button>
            </div>

            {nlStats && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatCard icon={Mail} label="Total envoyes" value={nlStats.total_emails_sent} />
                  <StatCard icon={Users} label="Destinataires uniques" value={nlStats.unique_recipients} />
                  <StatCard icon={Crown} label="Envoyes abonnes" value={nlStats.total_subscribers_sent} color="#FFCC00" />
                  <StatCard icon={Users} label="Envoyes non-abonnes" value={nlStats.total_non_subscribers_sent} color="#00BCD4" />
                </div>
                <p className="text-xs text-muted-foreground">Desabonnements : {nlStats.total_optouts}</p>

                {nlStats.campaigns?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider mb-2">Historique campagnes</h4>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {nlStats.campaigns.map((c, i) => (
                        <div key={i} className="border border-[#333] p-3 flex items-center justify-between text-sm">
                          <div>
                            <p className="font-bold text-xs">{c.campaign_id}</p>
                            <p className="text-xs text-muted-foreground">{c.sent_at?.slice(0, 16)}</p>
                          </div>
                          <div className="text-right text-xs">
                            <p>{c.total_sent} envoyes</p>
                            <p className="text-muted-foreground">{c.sent_subscribers} abo / {c.sent_non_subscribers} non-abo</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!nlStats && !nlStatsLoading && (
              <p className="text-sm text-muted-foreground text-center py-6">Cliquez sur "Charger" pour voir les statistiques</p>
            )}
          </div>

          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2">
              <Send size={16} className="text-[#FFCC00]" />
              Envoyer newsletter maintenant (2 segments)
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Abonnes Pro : email upsells (spins, opportunites, produits, fournisseurs).
              Non-abonnes : email pour s'abonner a 19 EUR/mois.
            </p>
            <button
              onClick={async () => {
                setTriggerNlLoading(true);
                try {
                  const token = localStorage.getItem("admin_token");
                  await axios.post(`${API}/admin/trigger-newsletter`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Newsletter en cours d'envoi aux 2 segments !");
                } catch { toast.error("Erreur"); }
                finally { setTriggerNlLoading(false); }
              }}
              disabled={triggerNlLoading}
              className="neo-button-primary w-full flex items-center justify-center gap-2"
              data-testid="trigger-newsletter-btn"
            >
              {triggerNlLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Envoyer la newsletter hebdomadaire
            </button>
          </div>
        </div>
      )}

      {tab === "send_email" && (
        <div className="space-y-6" data-testid="admin-send-email">
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <PenLine size={16} className="text-[#FF5722]" />
              Envoyer un email
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!campaignSubject || !campaignBody) return;
              setCampaignLoading(true);
              try {
                const token = localStorage.getItem("admin_token");
                const payload = {
                  subject: campaignSubject,
                  html_body: campaignBody.replace(/\n/g, "<br/>"),
                  target: campaignTarget,
                  custom_emails: campaignTarget === "custom" ? campaignCustomEmails.split(",").map(e => e.trim()).filter(Boolean) : [],
                };
                const res = await axios.post(`${API}/admin/send-email`, payload, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`${res.data.message}`);
                setCampaignSubject(""); setCampaignBody("");
              } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'envoi"); }
              finally { setCampaignLoading(false); }
            }} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Destinataires</label>
                <select
                  value={campaignTarget}
                  onChange={(e) => setCampaignTarget(e.target.value)}
                  className="neo-input w-full h-10 px-3 border-2 border-[#0A0A0A] bg-background"
                  data-testid="campaign-target"
                >
                  <option value="newsletter">Newsletter uniquement ({segmentCounts.newsletter ?? "?"})</option>
                  <option value="subscribers">Abonnes Premium uniquement ({segmentCounts.subscribers ?? "?"})</option>
                  <option value="all">Tous (utilisateurs + newsletter) ({segmentCounts.all ?? "?"})</option>
                  <option value="clients">Clients (tous les utilisateurs inscrits) ({segmentCounts.clients ?? "?"})</option>
                  <option value="custom">Emails personnalises</option>
                </select>
              </div>
              {campaignTarget === "custom" && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Emails (separes par virgule)</label>
                  <Input type="text" placeholder="email1@test.com, email2@test.com" value={campaignCustomEmails} onChange={(e) => setCampaignCustomEmails(e.target.value)} className="neo-input w-full" data-testid="campaign-custom-emails" />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Objet</label>
                <Input type="text" placeholder="Sujet de l'email..." value={campaignSubject} onChange={(e) => setCampaignSubject(e.target.value)} className="neo-input w-full" data-testid="campaign-subject" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Message (HTML supporté)</label>
                <textarea
                  value={campaignBody}
                  onChange={(e) => setCampaignBody(e.target.value)}
                  placeholder="Bonjour,&#10;&#10;Votre message ici...&#10;&#10;Cordialement,&#10;L'equipe Profimy"
                  rows={8}
                  className="neo-input w-full p-3 text-sm resize-none"
                  data-testid="campaign-body"
                  required
                />
              </div>
              <button type="submit" disabled={campaignLoading || !campaignSubject || !campaignBody} className="neo-button-primary w-full flex items-center justify-center gap-2" data-testid="campaign-send-btn">
                {campaignLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Envoyer la campagne
              </button>
            </form>
          </div>
        </div>
      )}

      {tab === "telegram" && (
        <div className="space-y-6" data-testid="admin-telegram">
          {/* Telegram subscribers list */}
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Phone size={16} className="text-[#0088cc]" />
              Abonnes Telegram ({telegramSubs.length})
            </h3>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {telegramSubs.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#0A0A0A]/10 text-sm">
                  <div>
                    <span className="font-bold">{s.email || "—"}</span>
                    <span className="text-muted-foreground text-xs ml-2">ID: {s.telegram_chat_id}</span>
                  </div>
                  <span className={`text-[10px] font-bold uppercase ${s.is_active ? "text-[#00E676]" : "text-[#FF6B35]"}`}>
                    {s.is_active ? "Actif" : "Inactif"}
                  </span>
                </div>
              ))}
              {telegramSubs.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Aucun abonne Telegram</p>}
            </div>
          </div>

          {/* Send Telegram message */}
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Send size={16} className="text-[#0088cc]" />
              Envoyer un message Telegram
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!telegramMsg) return;
              setTelegramLoading(true);
              try {
                const token = localStorage.getItem("admin_token");
                const res = await axios.post(`${API}/admin/telegram/send`, {
                  message: telegramMsg, target: telegramTarget,
                }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(res.data.message || `Envoye a ${res.data.total} abonnes`);
                setTelegramMsg("");
              } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
              finally { setTelegramLoading(false); }
            }} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Destinataire</label>
                <select
                  value={telegramTarget}
                  onChange={(e) => setTelegramTarget(e.target.value)}
                  className="neo-input w-full h-10 px-3 border-2 border-[#0A0A0A] bg-background"
                  data-testid="telegram-target"
                >
                  <option value="all">Tous les abonnes ({telegramSubs.length})</option>
                  {telegramSubs.map((s, i) => (
                    <option key={i} value={s.telegram_chat_id}>{s.email || s.telegram_chat_id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Message (HTML supporte)</label>
                <textarea
                  value={telegramMsg}
                  onChange={(e) => setTelegramMsg(e.target.value)}
                  placeholder="Votre message Telegram..."
                  rows={5}
                  className="neo-input w-full p-3 text-sm resize-none"
                  data-testid="telegram-message"
                  required
                />
              </div>
              <button type="submit" disabled={telegramLoading || !telegramMsg} className="neo-button-primary w-full flex items-center justify-center gap-2" data-testid="telegram-send-btn">
                {telegramLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Envoyer via Telegram
              </button>
            </form>
          </div>
        </div>
      )}

      {tab === "actions" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="admin-actions">
          {/* Grant Spins */}
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Gift size={18} className="text-[#FFD700]" /> Attribuer des spins
            </h3>
            <form onSubmit={handleGrantSpins} className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Email du membre</label>
                <Input
                  type="email"
                  placeholder="membre@email.com"
                  value={grantSpinEmail}
                  onChange={(e) => setGrantSpinEmail(e.target.value)}
                  className="neo-input w-full"
                  data-testid="grant-spin-email"
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Nombre de spins</label>
                <div className="flex gap-2">
                  {[1, 3, 5, 10, 20].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setGrantSpinCount(n)}
                      className={`px-3 py-1.5 text-sm font-bold border-2 ${grantSpinCount === n ? "bg-[#FFD700] text-[#0A0A0A] border-[#0A0A0A]" : "border-[#FFD700]/30 hover:bg-[#FFD700]/10"}`}
                    >
                      {n}
                    </button>
                  ))}
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={grantSpinCount}
                    onChange={(e) => setGrantSpinCount(parseInt(e.target.value) || 1)}
                    className="neo-input w-20 text-center"
                    data-testid="grant-spin-count"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={grantSpinLoading || !grantSpinEmail}
                className="neo-button-primary w-full flex items-center justify-center gap-2"
                data-testid="grant-spin-btn"
              >
                {grantSpinLoading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Attribuer {grantSpinCount} spin{grantSpinCount > 1 ? "s" : ""}
              </button>
            </form>
          </div>

          {/* Grant Access */}
          <div className="neo-card p-5">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Crown size={18} className="text-[#00E676]" /> Offrir un accès gratuit
            </h3>
            <form onSubmit={handleGrantAccess} className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Email du membre</label>
                <Input
                  type="email"
                  placeholder="membre@email.com"
                  value={grantAccessEmail}
                  onChange={(e) => setGrantAccessEmail(e.target.value)}
                  className="neo-input w-full"
                  data-testid="grant-access-email"
                  required
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Durée de l'accès</label>
                <div className="flex gap-2 flex-wrap">
                  {[{d:7,l:"7j"},{d:14,l:"14j"},{d:30,l:"1 mois"},{d:90,l:"3 mois"},{d:180,l:"6 mois"},{d:365,l:"1 an"}].map(({d,l}) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setGrantAccessDays(d)}
                      className={`px-3 py-1.5 text-sm font-bold border-2 ${grantAccessDays === d ? "bg-[#00E676] text-[#0A0A0A] border-[#0A0A0A]" : "border-[#00E676]/30 hover:bg-[#00E676]/10"}`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Expire le : <span className="font-bold text-foreground">{new Date(Date.now() + grantAccessDays * 86400000).toLocaleDateString("fr-FR")}</span>
              </div>
              <button
                type="submit"
                disabled={grantAccessLoading || !grantAccessEmail}
                className="neo-button-primary w-full flex items-center justify-center gap-2"
                data-testid="grant-access-btn"
              >
                {grantAccessLoading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
                Offrir {grantAccessDays}j d'accès
              </button>
            </form>
          </div>

          {/* Grant Upsell Access */}
          <div className="neo-card p-5 md:col-span-2">
            <h3 className="text-sm font-bold uppercase tracking-wider mb-4 flex items-center gap-2">
              <Package size={18} className="text-[#FF5722]" /> Offrir un acces gratuit (Upsells)
            </h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target;
              const email = form.upsell_email.value;
              const product_type = form.upsell_type.value;
              if (!email || !product_type) return;
              try {
                const token = localStorage.getItem("admin_token");
                const res = await axios.post(`${API}/admin/grant-upsell`, {
                  email, product_type, days: 365,
                }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(`${res.data.product_type} offert a ${email}`);
                form.reset();
              } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
            }} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Email du membre</label>
                  <Input type="email" name="upsell_email" placeholder="membre@email.com" className="neo-input w-full" data-testid="grant-upsell-email" required />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold block mb-1">Produit a offrir</label>
                  <select name="upsell_type" className="neo-input w-full h-10 px-3 border-2 border-[#0A0A0A] bg-background" data-testid="grant-upsell-type" required>
                    <option value="opportunities_premium">Opportunites Premium (4.90 EUR)</option>
                    <option value="products_500">500 Produits Gagnants (9 EUR)</option>
                    <option value="fournisseurs_300">300 Fournisseurs (9 EUR)</option>
                    <option value="marketplace_leboncoin">Produits Leboncoin (7 EUR)</option>
                    <option value="marketplace_vinted">Produits Vinted (7 EUR)</option>
                    <option value="marketplace_ebay">Produits eBay (7 EUR)</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="neo-button-primary w-full flex items-center justify-center gap-2" data-testid="grant-upsell-btn">
                <Gift size={16} /> Offrir l'acces gratuitement
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Error Logs Tab */}
      {tab === "errors" && (
        <div className="space-y-4" data-testid="admin-errors">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">Erreurs clients ({errorLogs.length})</h3>
            <button
              onClick={() => fetchAdminData(localStorage.getItem("admin_token"))}
              className="neo-button-secondary text-xs flex items-center gap-1"
            >
              <RefreshCw size={14} /> Actualiser
            </button>
          </div>
          {errorLogs.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8">Aucune erreur enregistree</p>
          ) : (
            <div className="space-y-2">
              {errorLogs.map((log, i) => (
                <div key={i} className="neo-card p-3 border-l-4" style={{ borderLeftColor: log.type === "react_crash" ? "#FF3B30" : log.type === "api_error" ? "#FF6B35" : "#FFCC00" }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold uppercase" style={{ color: log.type === "react_crash" ? "#FF3B30" : log.type === "api_error" ? "#FF6B35" : "#FFCC00" }}>
                      {log.type}
                    </span>
                    <span className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleString("fr-FR")}</span>
                  </div>
                  <p className="text-sm font-medium truncate">{log.message}</p>
                  <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                    {log.user_email && <span>User: {log.user_email}</span>}
                    {log.url && <span className="truncate max-w-[200px]">Page: {log.url}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Blog Auto Tab */}
      {tab === "blog_auto" && (
        <div className="space-y-4" data-testid="admin-blog-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">Articles Blog Auto-Generes</h3>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("admin_token");
                  await axios.post(`${API}/admin/blog/generate-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Generation d'article en cours...");
                } catch { toast.error("Erreur"); }
              }}
              className="neo-button-primary text-xs flex items-center gap-2"
              data-testid="generate-blog-btn"
            >
              <Zap size={14} /> Generer un article
            </button>
          </div>
          <p className="text-xs text-muted-foreground">CRON automatique : 5 articles/semaine (lun-ven). Chaque article est genere via GPT et optimise SEO.</p>
          <BlogAutoList />
        </div>
      )}

      {/* Social Media Tab */}
      {tab === "social" && (
        <div className="space-y-4" data-testid="admin-social">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">Posts Reseaux Sociaux</h3>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("admin_token");
                  await axios.post(`${API}/admin/social/generate-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Generation de post en cours...");
                } catch { toast.error("Erreur"); }
              }}
              className="neo-button-primary text-xs flex items-center gap-2"
              data-testid="generate-social-btn"
            >
              <Zap size={14} /> Generer un post
            </button>
          </div>
          <p className="text-xs text-muted-foreground">CRON : 1 post/jour auto-genere. Copiez-collez sur vos reseaux.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
            {[
              { name: "YouTube", url: "https://www.youtube.com/@ProfimyTM", color: "#FF0000" },
              { name: "Facebook", url: "https://www.facebook.com/profile.php?id=61588974627407", color: "#1877F2" },
              { name: "Instagram", url: "https://www.instagram.com/profimy_app", color: "#E4405F" },
              { name: "TikTok", url: "https://www.tiktok.com/@profimyapp", color: "#000000" },
              { name: "LinkedIn", url: "https://www.linkedin.com/in/joseph-torregrossa-40800a19a", color: "#0A66C2" },
              { name: "Telegram", url: "https://t.me/profimy", color: "#26A5E4" },
            ].map(s => (
              <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="neo-card p-3 flex items-center gap-2 hover:opacity-80" style={{ borderLeftColor: s.color, borderLeftWidth: 3 }}>
                <span className="text-xs font-bold">{s.name}</span>
              </a>
            ))}
          </div>
          <SocialPostsList />
        </div>
      )}

      {/* Affiliates Tab */}
      {tab === "affiliates" && (
        <div className="space-y-4" data-testid="admin-affiliates">
          <h3 className="font-bold text-lg">Programme Affilies (20% commission)</h3>
          <AffiliateStatsPanel />
        </div>
      )}

      {/* Prospects Tab */}
      {tab === "prospects" && (
        <div className="space-y-4" data-testid="admin-prospects">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">Prospection Automatique</h3>
            <button
              onClick={async () => {
                try {
                  const token = localStorage.getItem("admin_token");
                  await axios.post(`${API}/admin/prospects/find-now`, {}, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success("Recherche de prospects en cours...");
                } catch { toast.error("Erreur"); }
              }}
              className="neo-button-primary text-xs flex items-center gap-2"
              data-testid="find-prospects-btn"
            >
              <Search size={14} /> Trouver des prospects
            </button>
          </div>
          <p className="text-xs text-muted-foreground">CRON quotidien. Les prospects sont auto-ajoutes a la newsletter (tag: non_abonne).</p>
          <ProspectsPanel />
        </div>
      )}

      {/* Usage Analytics Tab */}
      {tab === "usage" && (
        <div className="space-y-4" data-testid="admin-usage">
          <h3 className="font-bold text-lg">Utilisation Quotidienne des Membres</h3>
          <DailyUsageReportPanel />
              <UsageAnalyticsPanel />
        </div>
      )}

      {/* Segments Tab */}
      {tab === "segments" && (
        <div className="space-y-4" data-testid="admin-segments">
          <h3 className="font-bold text-lg">Segmentation des Contacts</h3>
          <SegmentsPanel />
        </div>
      )}
    </div>
  );
}
